/**
 * Настройки рынка: чем платят, в каком поясе считают сутки, какой язык здесь
 * по умолчанию.
 *
 * ─── Почему это не константы в `format.ts` ──────────────────────────────────
 *
 * До B1 знак рубля, пояс «Европа/Москва» и тег `ru-RU` стояли буквами в
 * `shared/src/format.ts` — в девятнадцати местах. Пока продукт один, это
 * незаметно; на втором рынке каждая такая буква превращается в дефект, который
 * видит клиент: дирхамы, напечатанные рублём, и сутки, посчитанные по Москве
 * для того, кто живёт в Дубае.
 *
 * ─── Откуда это возьмётся на самом деле ─────────────────────────────────────
 *
 * Настройки рынка **отдаёт API** — так записано в плане глобала (этап B1:
 * «валюта/форматы из market-конфига, который отдаёт API»). Поля в контракте
 * пока нет, оно приезжает отдельным PR контракта, поэтому здесь стоит
 * заглушка: `RU_MARKET` — ровно то поведение, которое у РФ-кабинета сегодня, до
 * последней копейки и до последнего неразрывного пробела.
 *
 * Когда поле появится, меняется ровно одно место — `setMarket()` вызывается из
 * ответа сессии. Ни одна поверхность про это не узнает.
 */

import type { Locale } from './locale';

export type MarketCode = 'ru' | 'global';

export interface MarketConfig {
  /** Контур: РФ или глобал. Контуры не связаны (§15.10 спеки). */
  code: MarketCode;
  /** Язык, на котором рынок разговаривает, пока клиент не выбрал другой. */
  defaultLocale: Locale;
  /** Языки, между которыми на этом рынке вообще есть выбор. */
  locales: readonly Locale[];
  /** Код валюты ISO 4217 — для выгрузок, счетов и платёжного провайдера. */
  currency: string;
  /**
   * Знак валюты так, как его пишет рынок. Не берётся из `Intl`: `ru-RU`
   * печатает «9 900,00 ₽» с копейками и своим пробелом, а дизайн-система
   * требует «9 900 ₽» — копейки только там, где они есть (`data-viz.md` §8).
   */
  currencySymbol: string;
  /** Сколько мелких единиц в одной крупной: контракт держит деньги целыми. */
  currencyMinorUnits: number;
  /** Пояс, по которому продукт считает сутки замера и рисует даты. */
  timeZone: string;
}

/**
 * РФ. Заглушка B1: значения переписаны из `shared/src/format.ts`, чтобы
 * поведение кабинета не сдвинулось ни на знак.
 */
export const RU_MARKET: MarketConfig = {
  code: 'ru',
  defaultLocale: 'ru',
  locales: ['ru'],
  currency: 'RUB',
  currencySymbol: '₽',
  currencyMinorUnits: 100,
  timeZone: 'Europe/Moscow',
};

/**
 * Глобал. Тоже заглушка, и она честно помечена: до PR контракта её никто не
 * включает, но она нужна прямо сейчас — по ней видно, что тип описывает
 * настоящую разницу рынков, а не переименованные константы РФ.
 */
export const GLOBAL_MARKET: MarketConfig = {
  code: 'global',
  defaultLocale: 'en',
  locales: ['en', 'ar'],
  currency: 'AED',
  currencySymbol: 'AED',
  currencyMinorUnits: 100,
  timeZone: 'Asia/Dubai',
};

let active: MarketConfig = RU_MARKET;

/** Настройки рынка, на котором работает эта сборка. */
export function market(): MarketConfig {
  return active;
}

/**
 * Поставить настройки рынка. Единственный вызов — из ответа API, когда поле
 * появится в контракте; до тех пор сюда ходит только приёмка.
 */
export function setMarket(config: MarketConfig): void {
  active = config;
}
